import React, { useEffect, useState } from "react";
import { Navbar, Nav, Container, NavDropdown } from "react-bootstrap";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "./JobSeekerNavbar.css";
import Logo from '../Images/GudnetNewLogo.webp';
import BaseUrl from '../../ApiServices/BaseUrl';

const JobSeekerNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState("");
  const [userId, setUserId] = useState("");
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setIsLoggedIn(true);
      setUserName(user.first_name || "Profile");
      setUserId(user.id || "");
    }
  }, []);

  useEffect(() => {
    if (!userId) return;

    let isMounted = true;
    const fetchUnreadCount = async () => {
      try {
        const response = await fetch(`${BaseUrl}/message/unread-count/jobseeker/${userId}`);
        if (!response.ok) return;
        const data = await response.json();
        if (isMounted) {
          setUnreadMessageCount(Number(data?.unread_count || 0));
        }
      } catch (err) {
        console.error("Failed to fetch unread message count:", err);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 10000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [userId]);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const response = await fetch(`${BaseUrl}/api/job-seeker/${userId}`);
        if (!response.ok) {
          throw new Error('Failed to fetch profile data');
        }
        const data = await response.json();

        if (data.profile_photo) {
          data.profile_photo = `${BaseUrl}${data.profile_photo}`;
        }

        setProfileData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchProfileData();
    }
  }, [userId]);

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will be logged out of the system.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, log me out!",
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.removeItem("user");
        localStorage.removeItem("userId");
        localStorage.removeItem("userPlan");
        localStorage.removeItem("userStartDate");
        localStorage.removeItem("userEnddate");
        setIsLoggedIn(false);
        navigate("/");
        Swal.fire("Logged Out!", "You have been successfully logged out.", "success");
      }
    });
  };

  const handleMyAccount = () => navigate("/myaccount");
  const handleAddProfile = () => navigate("/personalinfo");
  const handleViewDetails = () => {
    if (userId) {
      navigate(`/allcandidatesdetails/${userId}`);
    }
  };
  const isActive = (path) => location.pathname === path;

  const isProfileActive = ["/", "/myaccount", "/personalinfo", "/combinedprofilecard", "/candidate"].includes(
    location.pathname
  );

  return (
    <Navbar expand="lg" bg="white" variant="light" fixed="top" className="shadow-sm py-2 custom-navbar">
      <Container>
        <Navbar.Brand
          className="d-flex align-items-center"
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/jobseeker-dashboard")}
        >
          <img
            src={Logo}
            alt="Gudnet Logo"
            style={{ height: '50px', objectFit: 'contain' }}
          />
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="main-navbar" />
        <Navbar.Collapse id="main-navbar">
          <Nav className="mx-auto nav-links">
            <Nav.Link
              href="/jobseeker-dashboard"
              className={isActive("/jobseeker-dashboard") ? "active-nav" : "nav-link-custom"}
            >
              DASHBOARD
            </Nav.Link>
            <Nav.Link
              href="/viewjobs"
              className={isActive("/viewjobs") ? "active-nav" : "nav-link-custom"}
            >
              JOBS
            </Nav.Link>
            <Nav.Link
              href="/appliedjobs"
              className={isActive("/appliedjobs") ? "active-nav" : "nav-link-custom"}
            >
              APPLIED JOBS
            </Nav.Link>

            <Nav.Link
              href="/agencies"
              className={isActive("/agencies") ? "active-nav" : "nav-link-custom"}
            >
              AGENCY
            </Nav.Link>

            <Nav.Link
              href="/jobseeker-messages"
              className={isActive("/jobseeker-messages") ? "active-nav" : "nav-link-custom"}
            >
              <span className="messages-nav-link">
                MESSAGES
                {unreadMessageCount > 0 && (
                  <span className="messages-unread-badge">{unreadMessageCount}</span>
                )}
              </span>
            </Nav.Link>

            <Nav.Link
              href="/candidate-subscription"
              className={isActive("/candidate-subscription") ? "active-nav" : "nav-link-custom"}
            >
              SUBSCRIPTION
            </Nav.Link>

            {/* ✅ Updated Dropdown Menu with complete content */}
            <NavDropdown title="CONTACT US" id="info-dropdown" className="news-dropdown">
              <NavDropdown.Item href="/jobseeker-aboutus">About Us</NavDropdown.Item>
              <NavDropdown.Item href="/jobseeker-contactus">Contact Us</NavDropdown.Item>
              <NavDropdown.Item href="/jobseeker-tips-news">Tips & News</NavDropdown.Item>
              <NavDropdown.Item href="/jobseeker-training">Training</NavDropdown.Item>
              <NavDropdown.Item href="/jobseeker-faqs">FAQs</NavDropdown.Item>
              <NavDropdown.Item href="/jobseeker-terms-and-conditions">Terms & Conditions</NavDropdown.Item>
              <NavDropdown.Item href="/jobseeker-privacy-policy">Privacy Policy</NavDropdown.Item>
              <NavDropdown.Item href="/jobseeker-returns-refunds-cancellation">
                Returns, Refunds & Cancellation Policy
              </NavDropdown.Item>
              <NavDropdown.Item href="/jobseeker-shipping-delivery-policy">
                Shipping & Delivery Policy
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>

          <div className="navbar-buttons d-flex flex-column gap-2">
            <NavDropdown
              title={
                <>
                  <i className="bi bi-person-circle me-1"></i> {userName}
                </>
              }
              id="profile-dropdown"
              className={`fw-semibold px-3 ${isProfileActive ? "active-nav" : ""}`}
            >
              <NavDropdown.Item
                onClick={handleMyAccount}
                className={isActive("/myaccount") ? "active-dropdown-item" : ""}
              >
                My Account
              </NavDropdown.Item>
              <NavDropdown.Item
                onClick={handleAddProfile}
                className={isActive("/personalinfo") ? "active-dropdown-item" : ""}
              >
                {profileData ? "Update Resume" : "Upload Resume"}
              </NavDropdown.Item>

              {profileData && (
                <NavDropdown.Item
                  onClick={handleViewDetails}
                  className={location.pathname.startsWith("/allcandidatesdetails") ? "active-dropdown-item" : ""}
                >
                  View Details
                </NavDropdown.Item>
              )}

              <NavDropdown.Divider />

              <NavDropdown.Item
                onClick={handleLogout}
                className="d-flex align-items-center text-danger"
              >
                <i className="bi bi-box-arrow-right me-2"></i> Logout
              </NavDropdown.Item>
            </NavDropdown>
          </div>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default JobSeekerNavbar;
